import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { PackageCheck, Plus, Star } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { ServiceStatusBadge } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useCreateService, useMyServices } from '@/features/services/api'
import { useMarketplaceCategories } from '@/features/marketplace/api'
import { ApiError } from '@/lib/api'
import { formatRupiah } from '@/lib/format'

export function MyServicesPage() {
  const { data, isLoading } = useMyServices()
  const [createOpen, setCreateOpen] = useState(false)
  const services = data?.services ?? []

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Jasa saya</h1>
          <p className="mt-1 text-muted">
            Paket siap pesan: pembeli langsung memesan tanpa tawar-menawar, dana masuk escrow.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          Buat jasa
        </Button>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : services.length === 0 ? (
        <Card>
          <EmptyState illustration="box"
            icon={<PackageCheck className="size-6" />}
            title="Belum ada jasa"
            description="Susun satu paket dengan harga dan lama pengerjaan yang jelas — itu yang membuat pembeli berani memesan langsung."
            action={<Button onClick={() => setCreateOpen(true)}>Buat jasa pertama</Button>}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <Card key={service.id}>
              <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center">
                {service.thumbnail_url ? (
                  <img
                    src={service.thumbnail_url}
                    alt=""
                    className="h-28 w-full shrink-0 rounded-xl object-cover sm:h-20 sm:w-32"
                  />
                ) : (
                  <div className="flex h-20 w-full shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand sm:w-32">
                    <PackageCheck className="size-6" />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <ServiceStatusBadge status={service.status} />
                    <Badge tone="brand">{service.category}</Badge>
                  </div>
                  <h2 className="mt-1.5 font-bold text-ink">{service.title}</h2>
                  <p className="mt-1 flex flex-wrap gap-x-4 text-sm text-muted">
                    <span>{service.packages.length} paket</span>
                    <span>{service.order_count} pesanan</span>
                    {service.rating_count > 0 ? (
                      <span className="inline-flex items-center gap-1">
                        <Star className="size-3.5 fill-amber-400 text-amber-400" />
                        {service.rating.toFixed(1)}
                      </span>
                    ) : null}
                    {service.starting_price > 0 ? (
                      <span className="font-semibold text-ink">mulai {formatRupiah(service.starting_price)}</span>
                    ) : null}
                  </p>
                </div>

                <Link to="/app/jasa/$listingId" params={{ listingId: service.id }}>
                  <Button size="sm">Kelola</Button>
                </Link>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <CreateServiceDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}

function CreateServiceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createService = useCreateService()
  const { data: categories } = useMarketplaceCategories()
  const toast = useToast()

  const [values, setValues] = useState({ title: '', description: '', category: '', thumbnail_url: '' })

  useEffect(() => {
    if (categories?.length && !values.category) {
      setValues((current) => ({ ...current, category: categories[0]?.slug ?? '' }))
    }
  }, [categories, values.category])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Buat jasa baru"
      description="Isi gambaran besarnya dulu; paket dan harga disusun pada langkah berikutnya."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={createService.isPending}
            disabled={values.title.trim().length < 8}
            onClick={() =>
              createService.mutate(
                { ...values, skills: [], thumbnail_url: values.thumbnail_url || undefined },
                {
                  onSuccess: () => {
                    toast.success('Jasa dibuat', 'Lanjutkan dengan menyusun paketnya.')
                    setValues({ title: '', description: '', category: values.category, thumbnail_url: '' })
                    onClose()
                  },
                  onError: (error) =>
                    toast.error('Gagal membuat jasa', error instanceof ApiError ? error.message : undefined),
                },
              )
            }
          >
            Buat dan lanjutkan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Judul jasa"
          required
          placeholder="Misalnya: Pembuatan dasbor penjualan siap pakai"
          value={values.title}
          onChange={(event) => setValues((current) => ({ ...current, title: event.target.value }))}
        />

        <Textarea
          label="Deskripsi"
          rows={5}
          hint="Minimal 80 karakter sebelum jasa dapat ditayangkan."
          placeholder="Apa yang Anda kerjakan, apa yang dibutuhkan dari pembeli, dan seperti apa hasil akhirnya."
          value={values.description}
          onChange={(event) => setValues((current) => ({ ...current, description: event.target.value }))}
        />

        <Select
          label="Bidang"
          value={values.category}
          onChange={(event) => setValues((current) => ({ ...current, category: event.target.value }))}
        >
          {categories?.map((category) => (
            <option key={category.slug} value={category.slug}>
              {category.name}
            </option>
          ))}
        </Select>

        <Input
          label="URL gambar sampul"
          placeholder="https://…"
          value={values.thumbnail_url}
          onChange={(event) => setValues((current) => ({ ...current, thumbnail_url: event.target.value }))}
        />
      </div>
    </Dialog>
  )
}
